import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Minus, Plus, Sparkles, Trash2, RefreshCw } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { recurringOrdersService } from '../../../services/recurringOrders.service';
import { prettyDate } from './recurringShared';

const REASON_LABELS = {
  not_on_menu: 'Not on this date\u2019s menu',
  discontinued: 'No longer available',
  qty_limit_exceeded: 'Quantity exceeds this date\u2019s limit',
};

// Occurrence review dialog — refreshes resolution on open so pricing and
// availability are live. Edits/swaps become per-date overrides; the schedule
// itself is never changed here.
export const OccurrenceReviewDialog = ({ open, onOpenChange, occurrenceId, canPlaceOrders, onDone }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [edited, setEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !occurrenceId) return;
    setLoading(true);
    setDetail(null);
    setEdited(false);
    recurringOrdersService.getOccurrence(occurrenceId)
      .then((d) => {
        setDetail(d);
        setLines((d.resolved_items || []).map((i) => ({ ...i })));
      })
      .catch(() => toast.error('Failed to load this date'))
      .finally(() => setLoading(false));
  }, [open, occurrenceId]);

  const dateProducts = useMemo(() => detail?.date_products || [], [detail]);
  const productMap = useMemo(
    () => Object.fromEntries(dateProducts.map((p) => [p.id, p])), [dateProducts]
  );

  const total = lines.reduce(
    (sum, l) => (l.available ? sum + (l.unit_price || 0) * (l.quantity || 0) : sum), 0
  );
  const hasUnavailable = lines.some((l) => !l.available);
  const isOpenStatus = detail && ['pending', 'needs_attention'].includes(detail.status);

  const setQty = (idx, qty) => {
    setEdited(true);
    setLines((cur) => cur.map((l, i) => (i === idx ? { ...l, quantity: Math.max(1, qty) } : l)));
  };

  const removeLine = (idx) => {
    setEdited(true);
    setLines((cur) => cur.filter((_, i) => i !== idx));
  };

  const swapLine = (idx, newPid) => {
    const p = productMap[newPid];
    if (!p) return;
    setEdited(true);
    setLines((cur) => cur.map((l, i) => (i === idx ? {
      product_id: p.id,
      product_name: p.name,
      quantity: l.quantity,
      unit_price: p.unit_price,
      available: true,
      unavailable_reason: null,
      image_url: p.image_url,
      pack_size: p.pack_size,
    } : l)));
  };

  const handleConfirm = async () => {
    if (lines.length === 0) return toast.error('Add at least one item before confirming.');
    if (hasUnavailable) return toast.error('Swap or remove the unavailable items first.');
    setBusy(true);
    try {
      const overrides = edited
        ? lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity }))
        : null;
      const res = await recurringOrdersService.confirmOccurrence(occurrenceId, overrides);
      toast.success(`Order ${res.order_number} placed for ${prettyDate(detail.delivery_date)}`, {
        action: {
          label: 'View order',
          onClick: () => navigate(`/client/orders/${res.order_id}`),
        },
        duration: 8000,
      });
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm this date');
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    setBusy(true);
    try {
      await recurringOrdersService.skipOccurrence(occurrenceId);
      toast.success('Date skipped — your schedule stays active');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to skip this date');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="occurrence-review-dialog">
        <DialogHeader>
          <DialogTitle>
            Review recurring order{detail ? ` — ${prettyDate(detail.delivery_date)}` : ''}
          </DialogTitle>
          <DialogDescription data-testid="occurrence-deadline-copy">
            {detail?.deadline_label
              ? `Confirm, edit, or skip ${detail.deadline_label}.`
              : 'Loading live availability…'}
          </DialogDescription>
        </DialogHeader>

        {loading || !detail ? (
          <div className="flex items-center justify-center py-12" data-testid="occurrence-loading">
            <RefreshCw className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {detail.menu?.mode === 'exclusive' && (
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 flex items-start gap-3" data-testid="review-exclusive-banner">
                <Sparkles className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-purple-900">{detail.menu.title || 'Special Menu'}</p>
                  <p className="text-xs text-purple-800/80">
                    This is a curated menu — only its items can be ordered for this date.
                  </p>
                </div>
              </div>
            )}

            {!isOpenStatus && (
              <div className="rounded-lg bg-gray-50 border p-3 text-sm text-gray-600" data-testid="occurrence-closed-notice">
                This date is {detail.status.replace('_', ' ')} and can no longer be changed.
              </div>
            )}

            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div
                  key={`${l.product_id}-${idx}`}
                  className={`p-3 border rounded-lg ${l.available ? '' : 'border-red-200 bg-red-50'}`}
                  data-testid={`review-line-${l.product_id}`}
                >
                  <div className="flex items-center gap-3">
                    {l.image_url && <img src={l.image_url} alt="" className="h-10 w-10 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{l.product_name}</p>
                      {l.available ? (
                        <p className="text-xs text-gray-500">
                          ${Number(l.unit_price || 0).toFixed(2)} / {l.pack_size || 'unit'}
                        </p>
                      ) : (
                        <p className="text-xs text-red-600 flex items-center gap-1" data-testid={`review-line-reason-${l.product_id}`}>
                          <AlertTriangle className="h-3 w-3" />
                          {REASON_LABELS[l.unavailable_reason] || 'Unavailable for this date'}
                        </p>
                      )}
                    </div>
                    {isOpenStatus && canPlaceOrders && (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(idx, l.quantity - 1)} data-testid={`review-qty-minus-${l.product_id}`}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm font-medium" data-testid={`review-qty-${l.product_id}`}>{l.quantity}</span>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(idx, l.quantity + 1)} data-testid={`review-qty-plus-${l.product_id}`}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeLine(idx)} data-testid={`review-remove-${l.product_id}`}>
                          <Trash2 className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {!l.available && isOpenStatus && canPlaceOrders && (
                    <div className="mt-2 flex items-center gap-2">
                      <Badge className="bg-red-100 text-red-700 shrink-0">Swap</Badge>
                      <Select onValueChange={(v) => swapLine(idx, v)}>
                        <SelectTrigger className="flex-1 h-8" data-testid={`review-swap-select-${l.product_id}`}>
                          <SelectValue placeholder="Choose a replacement from this date's menu…" />
                        </SelectTrigger>
                        <SelectContent>
                          {dateProducts
                            .filter((p) => !lines.some((x) => x.product_id === p.id))
                            .map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} — ${p.unit_price.toFixed(2)}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              {lines.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No items left — skip this date instead.</p>
              )}
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <p className="text-sm text-gray-500">
                {edited && 'Changes apply to this date only — your schedule is unchanged.'}
              </p>
              <p className="text-lg font-bold text-gray-900" data-testid="review-total">${total.toFixed(2)}</p>
            </div>
          </div>
        )}

        {detail && isOpenStatus && canPlaceOrders && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleSkip} disabled={busy} data-testid="review-skip-btn">
              Skip this date
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleConfirm}
              disabled={busy || hasUnavailable || lines.length === 0}
              data-testid="review-confirm-btn"
            >
              {busy ? 'Working…' : 'Confirm order'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
